/**
 * Pattern vs Safety Case ownership containment (presentation).
 *
 * PATTERN owns: recurrence, representative evidence, initial/new-activity review,
 * and the human decision "does this require management?"
 * SAFETY CASE owns: persistent problem, Control, implemented_at T, CCR follow-up, next action.
 * SAFETY SCAN owns: package, sign-off, signed Record.
 *
 * Epic 93/94 CA → Impact → Report remains available as legacy APIs; it must not
 * appear as the canonical Pattern lifecycle.
 */

import type { GroupedGuidedPanelId } from './groupedGuidedWorkflow';
import type {
  SafetyWorkflowProgress,
  SafetyWorkflowStepDefinition,
  SafetyWorkflowStepId,
} from '../safetyWorkflowStepMapping';

export const PATTERN_OWNERSHIP_BANNER_LABEL = 'Reviewer engaged';

export const PATTERN_FORBIDDEN_FOLLOWUP_LANGUAGE = [
  'approved, tracking follow-up',
  'tracking follow-up',
  'control underway',
  'monitoring after action',
] as const;

export const PATTERN_STATE_A_WORKFLOW_INTRO =
  'Review the recurring risk, inspect representative evidence, then decide whether this pattern requires ongoing management.';

export const PATTERN_STATE_B_WORKFLOW_INTRO =
  'This pattern is managed in the linked Safety Case. Investigate evidence is optional.';

export const PATTERN_MANAGEMENT_DECISION_LABEL = 'Does this require management?';
export const PATTERN_MANAGED_IN_CASE_LABEL = 'Managed in Safety Case';

export const PATTERN_CREATE_SAFETY_CASE_ACTION = 'Create Safety Case';
export const PATTERN_OPEN_SAFETY_CASE_ACTION = 'Open Safety Case';
export const PATTERN_OPEN_SAFETY_SCAN_ACTION = 'Open Safety Scan';
export const PATTERN_VERIFY_OBSERVATIONS_ACTION = 'Verify observations';
export const PATTERN_REVIEW_EVIDENCE_ACTION = 'Review representative evidence';
export const PATTERN_REVIEW_NEW_ACTIVITY_ACTION = 'Review new activity';
export const PATTERN_EXPLORE_OBSERVATIONS_ACTION = 'Explore observations';
export const PATTERN_TRIAGE_COMPLETE_LABEL = 'Triage complete';
export const PATTERN_CLEARED_LABEL = 'Cleared';
export const PATTERN_EXCLUDED_LABEL = 'Excluded';
export const PATTERN_ALREADY_MANAGED_LABEL = 'Already managed';
export const PATTERN_DEFERRED_LABEL = 'Deferred';

const TERMINAL_INVESTIGATE_CASE_DECISIONS = new Set([
  'rejected',
  'excluded',
  'already_managed',
]);
const TERMINAL_INVESTIGATE_REVIEW_OUTCOMES = new Set([
  'false_positive',
  'duplicate',
  'demo_or_test_activity',
  'expected_activity',
  'not_actionable',
]);

export function isDeferredInvestigateTriage(args: {
  caseDecision?: string | null;
  reviewOutcome?: string | null;
}): boolean {
  const decision = String(args.caseDecision || '').trim().toLowerCase();
  if (decision === 'deferred') return true;
  if (decision && TERMINAL_INVESTIGATE_CASE_DECISIONS.has(decision)) return false;
  return String(args.reviewOutcome || '').trim().toLowerCase() === 'insufficient_evidence';
}

export function isTerminalInvestigateClassification(args: {
  caseDecision?: string | null;
  reviewOutcome?: string | null;
}): boolean {
  const decision = String(args.caseDecision || '').trim().toLowerCase();
  if (TERMINAL_INVESTIGATE_CASE_DECISIONS.has(decision)) return true;
  if (decision === 'deferred' || decision === 'accepted') return false;
  return TERMINAL_INVESTIGATE_REVIEW_OUTCOMES.has(
    String(args.reviewOutcome || '').trim().toLowerCase(),
  );
}

export function investigateTerminalClassificationLabel(args: {
  caseDecision?: string | null;
  reviewOutcome?: string | null;
}): string | null {
  if (isDeferredInvestigateTriage(args)) return PATTERN_DEFERRED_LABEL;
  const decision = String(args.caseDecision || '').trim().toLowerCase();
  if (decision === 'rejected') return PATTERN_CLEARED_LABEL;
  if (decision === 'excluded') return PATTERN_EXCLUDED_LABEL;
  if (decision === 'already_managed') return PATTERN_ALREADY_MANAGED_LABEL;
  if (isTerminalInvestigateClassification(args)) return PATTERN_CLEARED_LABEL;
  return null;
}

export const PATTERN_EVIDENCE_COMPLETE_NO_CASE_COPY =
  'Evidence review is complete. Create or link a Safety Case if this condition requires ongoing management.';
export const PATTERN_EVIDENCE_COMPLETE_LINKED_CASE_COPY =
  'Evidence review is complete. This recurring safety issue is managed in the linked Safety Case.';

export const PATTERN_IMPACT_LEGACY_HELPER =
  'Legacy operator context or waiver only. This is not the canonical post-Control follow-up. Overview Since Control uses the Safety Case Control implementation time.';

export const PATTERN_IMPACT_NOT_ANCHOR_HELPER =
  'Saving this context does not set Control implementation time or start Since Control measurement.';

export const PATTERN_STATE_A_WORKFLOW_STEPS: SafetyWorkflowStepDefinition[] = [
  { id: 'detected', label: 'Risk pattern', compactLabel: 'Risk pattern' },
  { id: 'review', label: 'Evidence review', compactLabel: 'Evidence review' },
  { id: 'management_decision', label: PATTERN_MANAGEMENT_DECISION_LABEL, compactLabel: 'Management decision' },
];

export const PATTERN_STATE_B_WORKFLOW_STEPS: SafetyWorkflowStepDefinition[] = [
  { id: 'detected', label: 'Risk pattern', compactLabel: 'Risk pattern' },
  { id: 'review', label: 'Evidence review', compactLabel: 'Evidence review' },
  { id: 'managed_in_case', label: PATTERN_MANAGED_IN_CASE_LABEL, compactLabel: PATTERN_MANAGED_IN_CASE_LABEL },
];

export const PATTERN_OWNED_PANEL_IDS: GroupedGuidedPanelId[] = [
  'understand_risk',
  'verify_evidence',
];

export const PATTERN_CASE_PANEL_IDS: GroupedGuidedPanelId[] = [
  'understand_risk',
  'verify_evidence',
  'decide_action',
];

export const EPIC93_CONTAINED_PANEL_IDS: GroupedGuidedPanelId[] = [
  'decide_action',
  'track_impact',
  'report',
];

export function patternOwnershipHasLinkedCase(safetyCaseId?: string | null): boolean {
  return Boolean(String(safetyCaseId || '').trim());
}

export function resolvePatternOwnedWorkflowSteps(hasLinkedSafetyCase: boolean): SafetyWorkflowStepDefinition[] {
  return hasLinkedSafetyCase ? PATTERN_STATE_B_WORKFLOW_STEPS : PATTERN_STATE_A_WORKFLOW_STEPS;
}

export function resolvePatternOwnedPanelIds(hasLinkedSafetyCase: boolean): GroupedGuidedPanelId[] {
  return hasLinkedSafetyCase ? PATTERN_CASE_PANEL_IDS : PATTERN_OWNED_PANEL_IDS;
}

export function patternOwnedWorkflowIntro(hasLinkedSafetyCase: boolean): string {
  return hasLinkedSafetyCase ? PATTERN_STATE_B_WORKFLOW_INTRO : PATTERN_STATE_A_WORKFLOW_INTRO;
}

export function isEpic93PatternLifecycleStep(stepId: SafetyWorkflowStepId | string | null | undefined): boolean {
  return stepId === 'assign_action' || stepId === 'track_impact' || stepId === 'report';
}

export function copyContainsForbiddenFollowupLanguage(value: string | null | undefined): boolean {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return PATTERN_FORBIDDEN_FOLLOWUP_LANGUAGE.some((phrase) => normalized.includes(phrase));
}

export type PatternOwnedProgressInput = {
  hasLinkedSafetyCase: boolean;
  hasSelectedRecurringRisk?: boolean;
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
  hasNewActivityReview?: boolean;
  pendingObservationVerificationCount?: number | null;
  caseDecision?: string | null;
  reviewOutcome?: string | null;
};

export function patternHasAuthoritativePendingVerification(
  pendingObservationVerificationCount?: number | null,
): boolean {
  const pending = Number(pendingObservationVerificationCount);
  return Number.isFinite(pending) && pending > 0;
}

export type PatternOwnedNextActionKind =
  | 'review_evidence'
  | 'review_new_activity'
  | 'create_safety_case'
  | 'open_safety_case'
  | 'verify_observations';

export type PatternOwnedNextActionButton = {
  label: string;
  action: PatternOwnedNextActionKind;
};

export function patternNeedsEvidenceReview(args: PatternOwnedProgressInput): boolean {
  return !Boolean(args.representativeReviewComplete);
}

export function buildPatternOwnedWorkflowProgress(args: PatternOwnedProgressInput): SafetyWorkflowProgress {
  const hasSelected = Boolean(args.hasSelectedRecurringRisk ?? true);
  const reviewComplete = Boolean(args.representativeReviewComplete);
  const evidenceConfirmed = Boolean(args.evidenceConfirmed);
  const deferred = isDeferredInvestigateTriage(args);
  const terminalClear = isTerminalInvestigateClassification(args);
  const completedStepIds: SafetyWorkflowStepId[] = [];
  if (hasSelected) completedStepIds.push('detected');
  if (reviewComplete && !deferred) completedStepIds.push('review');

  if (args.hasLinkedSafetyCase) {
    if (reviewComplete) completedStepIds.push('review');
    return {
      currentStepId: 'managed_in_case',
      completedStepIds,
      nextActionLabel: 'Continue management in the linked Safety Case.',
      nextActionStepId: 'managed_in_case',
    };
  }

  if (!hasSelected) {
    return {
      currentStepId: 'detected',
      completedStepIds,
      nextActionLabel: 'Review the recurring risk pattern.',
      nextActionStepId: 'detected',
    };
  }

  if (deferred) {
    return {
      currentStepId: 'review',
      completedStepIds,
      nextActionLabel: 'Deferred — follow-up review is still required.',
      nextActionStepId: 'review',
    };
  }

  if (!reviewComplete) {
    return {
      currentStepId: 'review',
      completedStepIds,
      nextActionLabel: 'Review representative evidence and decide whether this pattern requires management.',
      nextActionStepId: 'review',
    };
  }

  if (evidenceConfirmed) {
    return {
      currentStepId: 'management_decision',
      completedStepIds,
      nextActionLabel: 'Create or link a Safety Case to manage this pattern.',
      nextActionStepId: 'management_decision',
    };
  }

  completedStepIds.push('management_decision');
  const clearedLabel = investigateTerminalClassificationLabel(args);
  return {
    currentStepId: 'management_decision',
    completedStepIds,
    nextActionLabel: terminalClear
      ? `${PATTERN_TRIAGE_COMPLETE_LABEL}. ${clearedLabel || PATTERN_CLEARED_LABEL}.`
      : 'This pattern does not require a Safety Case. Measurement can continue without creating a Case.',
    nextActionStepId: 'management_decision',
  };
}

export function buildPatternOwnedNextActionCopy(args: PatternOwnedProgressInput): {
  title: string;
  body: string;
  secondaryText: string;
} {
  const progress = buildPatternOwnedWorkflowProgress(args);
  if (args.hasLinkedSafetyCase) {
    return {
      title: 'Next required action',
      body: progress.nextActionLabel,
      secondaryText: 'Raw evidence remains optional. Formal Scan verification is not an Investigate task.',
    };
  }
  if (isDeferredInvestigateTriage(args)) {
    return {
      title: 'Next required action',
      body: progress.nextActionLabel,
      secondaryText: 'Deferred is not a cleared or completed Investigate decision.',
    };
  }
  if (isTerminalInvestigateClassification(args)) {
    return {
      title: 'Next required action',
      body: progress.nextActionLabel,
      secondaryText: 'This classification is persisted. It does not recreate the same review task.',
    };
  }
  if (args.representativeReviewComplete && args.evidenceConfirmed) {
    return {
      title: 'Next required action',
      body: progress.nextActionLabel,
      secondaryText: 'Corrective action, impact, and report packaging are not Pattern-owned stages.',
    };
  }
  if (args.representativeReviewComplete && !args.evidenceConfirmed) {
    return {
      title: 'Next required action',
      body: progress.nextActionLabel,
      secondaryText: 'A non-actionable Pattern decision does not create a Safety Case.',
    };
  }
  return {
    title: 'Next required action',
    body: progress.nextActionLabel,
    secondaryText: 'Confirm representative evidence before deciding whether this requires a Safety Case.',
  };
}

export function patternOwnedCurrentStageLabel(args: PatternOwnedProgressInput): string {
  const progress = buildPatternOwnedWorkflowProgress(args);
  if (progress.currentStepId === 'managed_in_case') return PATTERN_MANAGED_IN_CASE_LABEL;
  if (progress.currentStepId === 'management_decision') return PATTERN_MANAGEMENT_DECISION_LABEL;
  if (progress.currentStepId === 'review') return 'Evidence review';
  return 'Risk pattern';
}

export function patternOwnedNextActionButtons(args: PatternOwnedProgressInput & {
  hasCreateSafetyCase?: boolean;
  hasOpenSafetyCase?: boolean;
}): {
  primary: PatternOwnedNextActionButton | null;
  secondary: PatternOwnedNextActionButton | null;
} {
  const needsReview = patternNeedsEvidenceReview(args);
  const openCase: PatternOwnedNextActionButton | null = args.hasLinkedSafetyCase && args.hasOpenSafetyCase
    ? { label: PATTERN_OPEN_SAFETY_CASE_ACTION, action: 'open_safety_case' }
    : null;
  const verifyObservations: PatternOwnedNextActionButton | null = args.hasLinkedSafetyCase
    && patternHasAuthoritativePendingVerification(args.pendingObservationVerificationCount)
    ? { label: PATTERN_VERIFY_OBSERVATIONS_ACTION, action: 'verify_observations' }
    : null;
  const exploreEvidence: PatternOwnedNextActionButton | null = needsReview
    ? {
      label: args.hasNewActivityReview
        ? PATTERN_REVIEW_NEW_ACTIVITY_ACTION
        : PATTERN_EXPLORE_OBSERVATIONS_ACTION,
      action: args.hasNewActivityReview ? 'review_new_activity' : 'review_evidence',
    }
    : null;
  if (openCase) {
    return {
      primary: openCase,
      secondary: verifyObservations || exploreEvidence,
    };
  }
  if (isDeferredInvestigateTriage(args)) {
    return {
      primary: { label: PATTERN_REVIEW_EVIDENCE_ACTION, action: 'review_evidence' },
      secondary: null,
    };
  }
  if (isTerminalInvestigateClassification(args)) {
    return { primary: null, secondary: exploreEvidence };
  }
  if (needsReview) {
    return {
      primary: {
        label: PATTERN_REVIEW_EVIDENCE_ACTION,
        action: 'review_evidence',
      },
      secondary: null,
    };
  }
  if (!args.hasLinkedSafetyCase && args.evidenceConfirmed && args.hasCreateSafetyCase) {
    return {
      primary: { label: PATTERN_CREATE_SAFETY_CASE_ACTION, action: 'create_safety_case' },
      secondary: null,
    };
  }
  return { primary: null, secondary: null };
}

export function patternOwnedNextActionButton(args: PatternOwnedProgressInput & {
  hasCreateSafetyCase?: boolean;
  hasOpenSafetyCase?: boolean;
}): PatternOwnedNextActionButton | null {
  return patternOwnedNextActionButtons(args).primary;
}

export function patternEvidenceCompleteCopy(hasLinkedSafetyCase: boolean): string {
  return hasLinkedSafetyCase
    ? PATTERN_EVIDENCE_COMPLETE_LINKED_CASE_COPY
    : PATTERN_EVIDENCE_COMPLETE_NO_CASE_COPY;
}

export function shouldShowPatternCreateSafetyCase(args: {
  hasLinkedSafetyCase: boolean;
  evidenceConfirmed?: boolean;
}): boolean {
  return !args.hasLinkedSafetyCase && Boolean(args.evidenceConfirmed);
}
